<script setup>
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';
import { storeToRefs } from 'pinia';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import { useNaavixStore } from '~/naavix/store/naavix.store';

const props = defineProps({
  variant: {
    type: String,
    default: 'mini',
    validator(value) {
      return ['mini', 'full'].includes(value);
    },
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: true,
  },
  starterSuggestions: {
    type: Array,
    default: () => ([]),
  },
});

const emit = defineEmits(['close', 'variantChange']);

const { $date, $t } = useCommonImports();

const naavix_store = useNaavixStore();
const { active_chat_id, messages, flags, agent_data_updated_at, get_naavix_agent } = storeToRefs(naavix_store);
const { initialize_module_data, is_agent_data_present } = naavix_store;

const state = reactive({
  variant: 'mini',
  view: 'chat',
  is_loading: false,
});

const show_refresh = computed(() => {
  return dayjs().diff(dayjs(agent_data_updated_at.value), 'hours') > 24;
});

function createNewChat() {
  messages.value = [];
  state.view = 'chat';
  active_chat_id.value = nanoid();
}

async function onRefreshData() {
  await initialize_module_data();
}

function onViewChange(view) {
  state.view = view;
}

async function initializeAgent() {
  if (flags.value.is_agent_ready) {
    flags.value.is_agent_setup = true;
  }
  else {
    await initialize_module_data();
  }
}

watch(() => state.variant, () => {
  emit('variantChange', state.variant);
});

onMounted(async () => {
  state.variant = props.variant;
  if (flags.value.is_agent_setup)
    return;
  state.is_loading = true;
  flags.value.is_agent_setup = await is_agent_data_present();
  state.is_loading = false;
});
</script>

<template>
  <div
    class="flex flex-col"
    :class="[
      state.variant === 'full'
        ? 'rounded-none w-full h-full fixed inset-0 z-[1001]'
        : `rounded-lg p-[2px] gradient-background gradient-background-mini w-[488px] h-4/5 z-[1001] fixed right-4 bottom-20`,
    ]"
  >
    <div
      class="bg-white h-full z-10 flex"
      :class="state.variant === 'full' ? 'flex-row' : 'flex-col rounded-[6px]'"
    >
      <template v-if="state.is_loading">
        <div class="h-full p-4">
          <div class="text-sm font-semibold text-gray-900 flex justify-center items-center gap-2 mt-2">
            <IconHawkTaskmapperAi />
            NaaviX
            <template v-if="!get_naavix_agent.is_enabled">
              (disabled)
            </template>
          </div>
          <HawkLoader container_class="h-full" />
        </div>
      </template>
      <template v-else-if="flags.is_agent_setup">
        <NaavixChatsList
          v-show="state.view === 'all-chats' || state.variant === 'full'"
          :variant="state.variant"
          @create-new-chat="createNewChat"
          @view-change="onViewChange"
        />
        <div v-if="state.variant === 'full'" class="h-full w-px bg-gray-200" />
        <div v-show="state.view === 'chat' || state.variant === 'full'" class="h-full flex-1 flex flex-col">
          <NaavixChatHeader
            :variant="state.variant"
            @close="emit('close')"
            @create-new-chat="createNewChat"
            @variant-change="state.variant = $event"
            @view-change="onViewChange"
          />
          <div v-if="show_refresh" class="mx-4 my-2 px-2 py-1 bg-warning-50 border border-warning-300 rounded text-xs font-medium text-warning-700 flex justify-between">
            <span>
              {{ $t('Data is current as of') }} {{ $date(agent_data_updated_at, 'Do MMMM, YYYY') }}
            </span>
            <HawkLoader v-if="flags.is_agent_setting_up" container_class="" :height="4" :width="4" />
            <span v-else class="hover:text-warning-800 cursor-pointer h-4" @click="onRefreshData">
              <IconHawkRefreshCcwFive class="w-4 h-4 inline" />
              {{ $t('Refresh') }}
            </span>
          </div>
          <div class="flex-1 overflow-hidden">
            <NaavixChatStream
              :variant="state.variant"
              :title="props.title"
              :description="props.description"
              :starter-suggestions="props.starterSuggestions"
              @close="emit('close')"
            />
          </div>
        </div>
      </template>
      <NaavixSetupAgent
        v-else
        :is-agent-setting-up="flags.is_agent_setting_up"
        :is-agent-ready="flags.is_agent_ready"
        :is-agent-setup-failed="flags.is_agent_setup_failed"
        @initialize-agent="initializeAgent"
      />
    </div>
  </div>
</template>

<style scoped>
@import "../styles/stylesheet.scss";

@property --gradient-angle {
  syntax: "<angle>";
  initial-value: 0deg;
  inherits: false;
}

.gradient-background::before,
.gradient-background::after {
  content: "";
  position: absolute;
  inset: 0rem;
  z-index: -1;
  background: conic-gradient(
    from var(--gradient-angle),
    #A150D0 1.26%,
    #5055EA 18.63%,
    #45D1E0 38.35%,
    #41E64C 57.12%,
    #F2A13D 77.78%,
    #E43A9A 98.9%
  );
  border-radius: inherit;
  animation: rotation 10s linear infinite;
}

.gradient-background-full::before,
.gradient-background-full::after {
  inset: 1px;
}

.gradient-background::after {
  filter: blur(100px);
  opacity: 0.2;
}

@keyframes rotation {
  0% {
    --gradient-angle: 0deg;
  }
  100% {
    --gradient-angle: 360deg;
  }
}
</style>
